/**
 * 客服后台通知工具（从 AICS 原工作台 useNotification 迁移）
 * 能力：音效提醒（wav 优先，WebAudio 兜底）+ 浏览器桌面弹窗 + 偏好持久化
 */
const LS_KEY_SOUND = 'aics:notification:sound'
const LS_KEY_POPUP = 'aics:notification:popup'

function readBool(key, fallback) {
  try {
    const v = localStorage.getItem(key)
    if (v === null) return fallback
    return v === 'true'
  } catch (e) {
    return fallback
  }
}

export function getNotificationPrefs() {
  return {
    soundEnabled: readBool(LS_KEY_SOUND, true),
    popupEnabled: readBool(LS_KEY_POPUP, true)
  }
}

export function setNotificationPrefs(prefs) {
  if (prefs && prefs.soundEnabled !== undefined) {
    localStorage.setItem(LS_KEY_SOUND, String(prefs.soundEnabled))
  }
  if (prefs && prefs.popupEnabled !== undefined) {
    localStorage.setItem(LS_KEY_POPUP, String(prefs.popupEnabled))
  }
}

let audioCtx = null

function getAudioContext() {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return null
      audioCtx = new Ctx()
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {})
    }
    return audioCtx
  } catch (e) {
    return null
  }
}

function playBeepViaWebAudio() {
  const ctx = getAudioContext()
  if (!ctx) return
  try {
    const now = ctx.currentTime
    const gain = ctx.createGain()
    gain.connect(ctx.destination)
    gain.gain.setValueAtTime(0.4, now)
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3)

    const osc1 = ctx.createOscillator()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(880, now)
    osc1.connect(gain)
    osc1.start(now)
    osc1.stop(now + 0.15)

    const gain2 = ctx.createGain()
    gain2.connect(ctx.destination)
    gain2.gain.setValueAtTime(0.4, now + 0.15)
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.4)

    const osc2 = ctx.createOscillator()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(1100, now + 0.15)
    osc2.connect(gain2)
    osc2.start(now + 0.15)
    osc2.stop(now + 0.4)
  } catch (e) {
    /* silent */
  }
}

let htmlAudio = null
let htmlAudioFailed = false

function tryPlayHtmlAudio() {
  if (htmlAudioFailed) return false
  try {
    if (!htmlAudio) {
      htmlAudio = new Audio(process.env.BASE_URL + 'notification.wav')
      htmlAudio.volume = 0.6
      htmlAudio.addEventListener('error', () => { htmlAudioFailed = true })
    }
    htmlAudio.currentTime = 0
    const p = htmlAudio.play()
    if (p && p.catch) p.catch(() => { htmlAudioFailed = true })
    return !htmlAudioFailed
  } catch (e) {
    htmlAudioFailed = true
    return false
  }
}

export function playNotificationSound() {
  if (!readBool(LS_KEY_SOUND, true)) return
  if (!tryPlayHtmlAudio()) playBeepViaWebAudio()
}

/** 浏览器要求首次用户交互后才能播放声音，挂载时调用一次 */
export function unlockAudio() {
  const unlock = () => {
    getAudioContext()
    if (htmlAudio) {
      htmlAudio.play().then(() => {
        htmlAudio.pause()
        htmlAudio.currentTime = 0
      }).catch(() => {})
    }
  }
  document.addEventListener('click', unlock, { once: true })
  document.addEventListener('keydown', unlock, { once: true })
}

export function showBrowserNotification(title, body) {
  if (!readBool(LS_KEY_POPUP, true)) return
  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') return
  try {
    const opts = {
      body: body,
      icon: process.env.BASE_URL + 'favicon.ico',
      tag: 'aics-notification',
      renotify: true
    }
    const n = new Notification(title, opts)
    setTimeout(() => { try { n.close() } catch (e) { /* ignore */ } }, 6000)
  } catch (e) {
    /* silent */
  }
}

export function notifyWithSound(title, body) {
  playNotificationSound()
  showBrowserNotification(title, body)
}

export function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return Promise.resolve('unsupported')
  return Notification.requestPermission()
}

export function getPermissionStatus() {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission
}
